import type { AdminSessionResponse } from '@/contracts';

/**
 * The access token, and nowhere else it is written.
 *
 * A module-level variable is the whole storage layer, and that is deliberate. It
 * is what a browser tab can hold and a disk cannot: `localStorage` would be
 * readable by any script on the page, and an XSS in a console that grants
 * verification badges and deletes candidates (C5) would walk off with an
 * eight-hour session.
 *
 * What survives a reload is the **refresh** token, in an httpOnly cookie the API
 * sets and JavaScript cannot read (Backend #25). So a hard refresh costs one
 * round trip to `/admin/auth/refresh` and no password. That is the same property
 * the Next.js console had by keeping the token on its own server, kept now that
 * there is no server.
 *
 * The consequence to hold on to: **anything that needs the token must ask this
 * module for it.** A component that receives it as a prop has put it somewhere
 * this module cannot clear.
 */
let accessToken: string | null = null;

/**
 * Whether this browser has held a session since it last signed out.
 *
 * Not the token and not a secret — a single flag in `localStorage` that says
 * "it is worth asking `/admin/auth/refresh`". Without it every first visit to
 * the login page probed the refresh endpoint, got the 401 it was always going
 * to get, and logged it to the console before anyone had typed a thing. The
 * refresh cookie itself is httpOnly, so this is the only way to know not to
 * ask. A session that expired server-side still costs one 401, once, and then
 * the flag is gone.
 */
export const ADMIN_SESSION_HINT_KEY = 'kadir-admin:had-session';

function readHint(): boolean {
  try {
    return window.localStorage.getItem(ADMIN_SESSION_HINT_KEY) === '1';
  } catch {
    // Private mode or storage disabled: assume there might be one, which is
    // the old behaviour and the safe direction.
    return true;
  }
}

function writeHint(present: boolean): void {
  try {
    if (present) window.localStorage.setItem(ADMIN_SESSION_HINT_KEY, '1');
    else window.localStorage.removeItem(ADMIN_SESSION_HINT_KEY);
  } catch {
    // Nothing to do: the probe simply happens next time.
  }
}

/** Fires whenever the session appears or disappears, so the app can re-render. */
type Listener = () => void;
const listeners = new Set<Listener>();

function announce(): void {
  for (const listen of listeners) listen();
}

export const adminSession = {
  get token(): string | null {
    return accessToken;
  },

  get isSignedIn(): boolean {
    return accessToken !== null;
  },

  /** Whether a cold load should try to restore a session at all. */
  get mayHaveSession(): boolean {
    return readHint();
  },

  /** Records the pair the API just issued. The refresh half is already a cookie. */
  start(session: AdminSessionResponse): void {
    accessToken = session.accessToken;
    writeHint(true);
    announce();
  },

  /**
   * Forgets the token. It does *not* call the API — a caller that wants the
   * server session revoked calls `logout`, and a caller reacting to a 401 has
   * nothing left to revoke with.
   */
  clear(): void {
    accessToken = null;
    writeHint(false);
    announce();
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
